/**
 * Card → Lot Highlight Plugin for Native Interactive Site Map
 *
 * Hovering a Floor Plan, Quick Move-In, or Sold Home card in the listing panel temporarily
 * highlights the lot(s) that card corresponds to on the map.
 *
 * This plugin only detects card hover and resolves it to sitePlanLotId(s), by re-querying the
 * same selectors that already built the card's own content (selectFilteredHousePlans /
 * selectFilteredHousingUnits, chip-filtered) plus selectLotsById to translate the integer lotId
 * those selectors (and, for Sold cards, data-lot-id directly) use into the sitePlanLotId string
 * the SVG understands. It never touches the SVG or any CSS class directly — that stays owned by
 * lotInteraction.plugin.js, which subscribes to the cardLotHovered$ Subject this plugin emits on
 * (see index.js for where that Subject is created and shared via context.services). The mobile
 * bottom-sheet Sold carousel's swipe-driven equivalent is a separate concern, owned by
 * soldCardCarouselHighlight.plugin.js, which emits onto the same Subject.
 *
 * What this plugin does NOT do:
 * - Does not create or hold a lotColors instance — a second one would independently track
 *   "currently hovered" state and could desync from the native map hover's own instance.
 * - Does not read data-plan-id/data-housing-unit-id/data-lot-id as a source of lot associations —
 *   only as an opaque identity key. Which lot(s) that key maps to right now always comes from the
 *   store.
 * - Does not run on mobile — hover has no touch equivalent, and mobile doesn't show the map and
 *   the listing panel side by side. 
 * 
 * Desktop-only is enforced by gating on `context.state.isMobile` 
 *
 * @module nativeInteractiveSiteMap/plugins/cardLotHighlight.plugin
 */

import ListingType from 'enums/listingType';
import createCardHoverInteractionHandlers from '../handlers/cardHoverInteractionHandlers';
import {
  selectFilteredHousePlans, selectFilteredHousingUnits, selectLotsById, selectSoldLotsById,
} from '../redux/selectors';

function resolveSitePlanLotIdsForPlan(state, planId) {
  const plan = selectFilteredHousePlans(state).find((candidate) => String(candidate.planId) === planId);
  if (!plan) {
    return new Set();
  }
  const lotsById = selectLotsById(state);
  const sitePlanLotIds = plan.lotIds
    .map((lotId) => lotsById[lotId]?.sitePlanLotId)
    .filter(Boolean);
  return new Set(sitePlanLotIds);
}

function resolveSitePlanLotIdsForHousingUnit(state, specificationId) {
  const unit = selectFilteredHousingUnits(state).find(
    (candidate) => String(candidate.specificationId) === specificationId
  );
  if (!unit) {
    return new Set();
  }
  const sitePlanLotId = selectLotsById(state)[unit.lotId]?.sitePlanLotId;
  return sitePlanLotId ? new Set([sitePlanLotId]) : new Set();
}

// data-lot-id on a sold-card-group is already the internal lotId (see
// services/listViewContentService.js#renderSoldHomeCards). Sold lots live in their own API
// section and are never present in selectLotsById (AvailableLots only), so this resolves against
// selectSoldLotsById instead.
function resolveSitePlanLotIdsForSoldHome(state, lotId) {
  const sitePlanLotId = selectSoldLotsById(state)[lotId]?.sitePlanLotId;
  return sitePlanLotId ? new Set([sitePlanLotId]) : new Set();
}

function resolveSitePlanLotIds(getState, { type, id }) {
  const currentState = getState();
  if (type === ListingType.HousePlan) {
    return resolveSitePlanLotIdsForPlan(currentState, id);
  }
  if (type === ListingType.HousingUnit) {
    return resolveSitePlanLotIdsForHousingUnit(currentState, id);
  }
  return resolveSitePlanLotIdsForSoldHome(currentState, id);
}

export default function createCardLotHighlightPlugin() {
  const state = {
    panel: null,
    handlers: null,
  };

  return {
    name: 'card-lot-highlight',

    async start(context) {
      if (context.state.isMobile) {
        return;
      }

      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }

      const { getState, cardLotHovered$ } = context.services;

      state.handlers = createCardHoverInteractionHandlers({
        onCardHovered(identity) {
          const sitePlanLotIds = resolveSitePlanLotIds(getState, identity);
          cardLotHovered$.next(sitePlanLotIds.size > 0 ? sitePlanLotIds : null);
        },
        onCardLeft() {
          cardLotHovered$.next(null);
        },
      });

      state.handlers.bind(state.panel);
    },

    async stop() {
      if (state.panel && state.handlers) {
        state.handlers.unbind(state.panel);
      }
      state.panel = null;
      state.handlers = null;
    },
  };
}
